// Sequential DISPLAY numbering of the selected run — what the user reads on
// board chips, tree rows, and the scrubber counter. Dense 1..K over the
// selected walk's forward steps, no gaps: the canonical stepIndex (gapped,
// all-branches) stays the internal identity/join key, never shown.
//
// Unnumbered rows: compensation steps (the failure back-flow — rendered ↩)
// and anything outside the selected walk (alternative branches — rendered ·).

import type { WalkResult } from "@archlang/engine";

export interface DisplayNumbering {
  /** canonical stepIndex → 1-based position in the selected run. */
  readonly byIndex: ReadonlyMap<number, number>;
  /** Count of numbered (forward, selected) steps. */
  readonly total: number;
}

export function buildDisplayNumbering(walk: WalkResult): DisplayNumbering {
  const byIndex = new Map<number, number>();
  let n = 0;
  for (const s of walk.steps) {
    if (s.modifiers.compensation) continue; // failure path — shown as ↩
    n += 1;
    byIndex.set(s.stepIndex, n);
  }
  return { byIndex, total: n };
}

const cache = new WeakMap<WalkResult, DisplayNumbering>();

/** Cached per WalkResult — safe to call from every row/chip render. */
export function displayNumberingFor(walk: WalkResult): DisplayNumbering {
  let n = cache.get(walk);
  if (!n) {
    n = buildDisplayNumbering(walk);
    cache.set(walk, n);
  }
  return n;
}

/** The badge text for one step: sequential run number, `↩` (compensation),
 *  or `·` (not on the selected run — e.g. an unselected alternative). */
export function displayLabelFor(
  walk: WalkResult,
  stepIndex: number,
  compensation: boolean,
): string {
  const n = displayNumberingFor(walk).byIndex.get(stepIndex);
  if (n !== undefined) return String(n);
  return compensation ? "↩" : "·";
}
